import React, { useState } from 'react';
import { ToggleLeft, ToggleRight, UserCheck, X } from 'lucide-react';
import { formatAmountText } from '../../../lib/utils';
import { useAmountInputCaret } from '../../../lib/amountInputCaret';

export type TrainerBudgetInfo = {
    id: string;
    name: string;
    role: string;
    budgetEnabled: boolean;
    monthlyLimit: number;
    usedThisMonth: number;
};

const point = (value: number) => `${value.toLocaleString()}P`;

const MetricTile = ({ label, value, description }: { label: string; value: string; description: string }) => (
    <div className="rounded-[8px] border border-[#E2E8F0] bg-white p-[14px]">
        <p className="text-[12px] font-bold text-[#64748B]">{label}</p>
        <p className="mt-[4px] truncate text-[18px] font-bold text-[#111827]">{value}</p>
        <p className="mt-[2px] text-[11px] text-[#94A3B8]">{description}</p>
    </div>
);

const UsageBar = ({ value }: { value: number }) => {
    const color = value >= 90 ? '#EF4444' : value >= 70 ? '#F59E0B' : '#3D73DD';
    return (
        <div className="mt-[9px] h-[8px] overflow-hidden rounded-full bg-[#E9EEF5]">
            <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.max(0, value))}%`, backgroundColor: color }} />
        </div>
    );
};

const TrainerPermissionModal = ({
    trainers,
    isLoading = false,
    onToggle,
    onLimitChange,
    onShowUsage,
    onClose,
}: {
    trainers: TrainerBudgetInfo[];
    isLoading?: boolean;
    onToggle: (trainerId: string) => void;
    /** 입력이 비면 `null`. 부모는 그때 저장 타이머를 걸지 않는다(0P 자동 저장 방지). */
    onLimitChange: (trainerId: string, limit: number | null) => void;
    onShowUsage: (trainerName: string) => void;
    onClose: () => void;
}) => {
    /**
     * 한도를 지운 강사. `Number('') === 0` 이라 숫자만으로는 "빈칸"과 0P 를 구분할 수 없어
     * 따로 들고 있는다. 입력은 한 번에 하나만 포커스되므로 id 하나면 충분하다.
     */
    const [blankTrainerId, setBlankTrainerId] = useState<string | null>(null);
    const amountCaret = useAmountInputCaret({ allowDecimal: true });
    const enabledTrainers = trainers.filter((trainer) => trainer.budgetEnabled);
    const budgetLimit = enabledTrainers.reduce((sum, trainer) => sum + trainer.monthlyLimit, 0);
    const budgetUsed = enabledTrainers.reduce((sum, trainer) => sum + trainer.usedThisMonth, 0);

    return (
        <div className="fixed inset-0 z-[85] flex items-center justify-center bg-[rgba(15,23,42,0.38)] px-[24px] py-[24px]">
            <div className="flex max-h-[88vh] w-full max-w-[760px] flex-col rounded-[8px] bg-white shadow-[0_24px_60px_rgba(15,23,42,0.24)]">
                <div className="flex items-start justify-between border-b border-[#E2E8F0] px-[24px] py-[18px]">
                    <div>
                        <div className="flex items-center gap-[8px]">
                            <UserCheck size={19} color="#3D73DD" />
                            <h2 className="text-[18px] font-bold leading-[26px] text-[#1F2937]">강사별 센터 예산 사용 권한</h2>
                        </div>
                        <p className="mt-[2px] text-[13px] leading-[20px] text-[#64748B]">
                            권한을 끄면 해당 강사는 본인 결제로만 선물할 수 있습니다. 이번 달 사용 현황을 보고 한도를 조정하세요.
                        </p>
                    </div>
                    <button type="button" aria-label="닫기" onClick={onClose} className="rounded-[8px] p-[6px] hover:bg-[#F8FAFC]">
                        <X size={20} color="#64748B" />
                    </button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto px-[24px] py-[18px]">
                    <div className="grid grid-cols-4 gap-[10px]">
                        <MetricTile label="권한 보유 강사" value={`${enabledTrainers.length} / ${trainers.length}명`} description="센터 예산으로 선물 가능" />
                        <MetricTile label="월 한도 합계" value={point(budgetLimit)} description="권한 보유 강사 기준" />
                        <MetricTile label="이번 달 사용 합계" value={point(budgetUsed)} description="강사 발송분" />
                        <MetricTile label="한도 소진율" value={`${budgetLimit > 0 ? Math.round((budgetUsed / budgetLimit) * 100) : 0}%`} description="한도 합계 대비" />
                    </div>

                    <div className="mt-[16px] space-y-[9px]">
                        {trainers.length === 0 && (
                            <p className="py-[34px] text-center text-[13px] text-[#94A3B8]">
                                {isLoading ? '강사 목록을 불러오는 중입니다…' : '등록된 강사가 없습니다.'}
                            </p>
                        )}
                        {trainers.map((trainer) => {
                            const usedRate = trainer.monthlyLimit > 0 ? Math.round((trainer.usedThisMonth / trainer.monthlyLimit) * 100) : 0;
                            return (
                                <div key={trainer.id} className={`flex min-h-[74px] items-center gap-[16px] rounded-[8px] border px-[14px] py-[12px] ${trainer.budgetEnabled ? 'border-[#E2E8F0] bg-white' : 'border-[#E5E7EB] bg-[#FAFAFA]'}`}>
                                    <div className="flex w-[180px] shrink-0 items-center gap-[10px]">
                                        <button type="button" aria-label={`${trainer.name} 예산 권한`} onClick={() => onToggle(trainer.id)}>
                                            {trainer.budgetEnabled ? <ToggleRight size={34} color="#3D73DD" /> : <ToggleLeft size={34} color="#94A3B8" />}
                                        </button>
                                        <div>
                                            <p className="text-[14px] font-bold text-[#1F2937]">{trainer.name}</p>
                                        </div>
                                    </div>
                                    {trainer.budgetEnabled ? (
                                        <>
                                            <label className="flex w-[180px] shrink-0 items-center gap-[7px]">
                                                <span className="text-[12px] text-[#64748B]">월 한도</span>
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    value={blankTrainerId === trainer.id ? '' : formatAmountText(String(trainer.monthlyLimit))}
                                                    onChange={(event) => {
                                                        // 원칙 1 — 패턴에 안 맞으면 값을 아예 바꾸지 않는다.
                                                        const digits = amountCaret.changeAmount(event, blankTrainerId === trainer.id ? '' : trainer.monthlyLimit);
                                                        if (digits == null) return;
                                                        // 원칙 2 — 빈칸은 0 으로 바꾸지 않고 null 로 알려 저장을 막는다.
                                                        setBlankTrainerId(digits === '' ? trainer.id : null);
                                                        onLimitChange(trainer.id, digits === '' ? null : Number(digits));
                                                    }}
                                                    // 자동 저장 경로다. 붙여넣기는 정규형만 받는다(CSV 조각 차단).
                                                    onPaste={amountCaret.pasteAmount}
                                                    onDrop={amountCaret.dropAmount}
                                                    onBlur={() => setBlankTrainerId((prev) => (prev === trainer.id ? null : prev))}
                                                    className="h-[32px] min-w-0 flex-1 rounded-[7px] border border-[#CBD5E1] px-[8px] text-right text-[12px] font-bold outline-none focus:border-[#3D73DD]"
                                                />
                                                <span className="text-[12px] text-[#64748B]">P</span>
                                            </label>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex justify-between text-[11px] text-[#64748B]">
                                                    <span>이번 달 사용</span>
                                                    <span><b className={usedRate >= 90 ? 'text-[#DC2626]' : 'text-[#334155]'}>{point(trainer.usedThisMonth)}</b> / {point(trainer.monthlyLimit)}</span>
                                                </div>
                                                <UsageBar value={usedRate} />
                                                {usedRate >= 90 && <p className="mt-[4px] text-[11px] text-[#DC2626]">한도가 거의 소진되었습니다. 소진되면 추가 사용이 차단됩니다.</p>}
                                            </div>
                                        </>
                                    ) : (
                                        <p className="min-w-0 flex-1 text-[12px] text-[#94A3B8]">권한 꺼짐 · 강사 앱에서 센터 예산 결제 옵션이 표시되지 않습니다.</p>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => onShowUsage(trainer.name)}
                                        className="h-[32px] shrink-0 rounded-[7px] border border-[#CBD5E1] bg-white px-[10px] text-[11px] font-bold text-[#475569] hover:bg-[#F8FAFC]"
                                    >
                                        사용내역
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="border-t border-[#E2E8F0] px-[24px] py-[14px] text-right">
                    <button type="button" onClick={onClose} className="h-[40px] rounded-[8px] bg-[#3D73DD] px-[18px] text-[13px] font-bold text-white">확인</button>
                </div>
            </div>
        </div>
    );
};

export default TrainerPermissionModal;
